import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactGA from "react-ga4";
import newLogo from '../assets/images/new-logo.png';

const TermsPage = () => {
  useEffect(() => {
    document.title = "Artie - Terms and Conditions";
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans text-neutral-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" onClick={() => ReactGA.event({ category: "Navigation", action: "Home", label: "Logo" })} className="flex items-center gap-2 group">
            <img src={newLogo} alt="Artie" className="w-40 h-40 object-contain" />
          </Link>
          <Link to="/" onClick={() => ReactGA.event({ category: "Navigation", action: "Home", label: "Back to Home" })} className="text-sm font-medium text-neutral-500 hover:text-neutral-900 transition-colors font-geist">
            Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 md:py-20">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 font-geist">Terms of Service</h1>
        <p className="text-neutral-500 mb-12 font-geist">Last Updated: January 16, 2026</p>

        <div className="space-y-8 font-geist text-neutral-800 leading-relaxed">
          <p>
            These Terms of Service govern your access to and use of the Artie website. By accessing or using the website, you agree to these Terms.
          </p>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-neutral-900">Eligibility</h2>
            <p className="mb-6">
              You must be at least 18 years old to access or use the website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-neutral-900">Purpose of the Website</h2>
            <p className="mb-4">
              The website is a marketing and informational site. It does not currently offer products, user accounts, uploads, or payments. As we develop and launch new products and services, we may update these Terms of Service and will do so by updating this site.
            </p>
            <p className="mb-6">
              If and when products or services are introduced, use of those offerings may be governed by separate terms and privacy policies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-neutral-900">Acceptable Use</h2>
            <p className="mb-6">
              You may use the website only for lawful purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-neutral-900">Restrictions on Access and Use</h2>
            <p className="mb-4">You agree not to:</p>
            <ul className="list-disc pl-5 space-y-2 mb-6 text-neutral-700">
              <li>Use automated tools, scripts, or bots to access or scrape the website</li>
              <li>Access the website in a manner that imposes unreasonable load on our infrastructure</li>
              <li>Use the website for competitive analysis, benchmarking, or similar purposes without permission</li>
              <li>Attempt to bypass security measures or authentication systems</li>
              <li>Reverse-engineer or disassemble any portion of the website</li>
            </ul>
            <p className="mb-6">
              We reserve the right to suspend or terminate access to the website at our sole discretion, with or without notice, if we believe you have violated these Terms or engaged in unlawful conduct.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-neutral-900">Intellectual Property Rights</h2>
            <p className="mb-4">
              All content on the website, including text, graphics, logos, design elements, and features, is owned by Artie Incorporated or its licensors and is protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p className="mb-4">
              You may not reproduce, distribute, modify, translate, or create derivative works of site content without our prior written consent. Limited copying for personal, non-commercial use is permitted, provided you retain all copyright and proprietary notices.
            </p>
            <p className="mb-6">
              You agree not to remove, obscure, or alter any proprietary notices or labels.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-neutral-900">Disclaimers</h2>
            <p className="mb-4">
              The website and all content are provided on an “as is” and “as available” basis without warranties of any kind, express or implied. We disclaim all warranties, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
            </p>
            <p className="mb-4">We make no guarantees regarding:</p>
            <ul className="list-disc pl-5 space-y-2 mb-4 text-neutral-700">
              <li>Availability, uptime, or uninterrupted access to the website</li>
              <li>Accuracy, completeness, or currency of any content or information</li>
              <li>Absence of errors, bugs, or security vulnerabilities</li>
              <li>That the website will meet your expectations or requirements</li>
            </ul>
            <p className="mb-6">
              Content on the site is for informational purposes only and does not constitute professional advice.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-neutral-900">Limitation of Liability</h2>
            <p className="mb-6">
              To the fullest extent permitted by law, Artie will not be liable for indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the website. In no event shall Artie's total liability to you exceed the greater of (a) one hundred dollars ($100) or (b) the amount you have paid to Artie in the preceding 12 months.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-neutral-900">Governing Law</h2>
            <p className="mb-6">
              These Terms are governed by the laws of the State of Delaware, without regard to conflict of laws principles.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-neutral-900">Changes to These Terms</h2>
            <p className="mb-6">
              We may update these Terms from time to time. When we do, we will revise the “Last Updated” date at the top of this page. Your continued use of the website constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-neutral-900">Contact</h2>
            <p className="mb-6">
              You may contact us for questions about these Terms at <a href="mailto:contact@artie.co" className="text-neutral-900 underline underline-offset-2 hover:text-neutral-600">contact@artie.co</a>.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white pt-12 pb-12 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-neutral-400 font-geist">
            © 2026 Artie. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;